import type { Intent } from '@wand/shared/text/intent.js';

/**
 * What somebody MEANS when they ask for something to be made.
 *
 * `tanjong-intents.ts` recognises one QUESTION asked many ways. This recognises one
 * INSTRUCTION asked many ways, and it rests on a different observation: THE VERB WAS NEVER
 * THE DISCRIMINATOR. "Build an autonomous agent called SIU Triage Sentinel" and "commission
 * an SIU triage sentinel" ask for the same thing. "Build a process for claims document
 * completeness" and "Create me an agentic process to check the completeness of the documents"
 * ask for a different same thing. What separates the two families is the ARTEFACT — a
 * process or a watcher — not the word in front of it.
 *
 *     MAKE × ARTEFACT × DOMAIN,  minus NOT_A_MAKE and the other artefact's head nouns
 *
 * MAKE and the veto are shared by all of them. ARTEFACT decides build-versus-commission and
 * the two families veto each other's head noun, so the arbitration is structural rather than
 * a bet on registration order. DOMAIN decides which of the five, or which watcher.
 *
 * FOUR RULES, ALL OF THEM LEARNED THE HARD WAY:
 *
 * · NO STEMMER, same as the shared matcher: enumerate the surface forms. And enumerate the
 *   HYPHENATED ones too — the normaliser keeps `-`, so `risk-scoring` is one token that
 *   matches neither `risk` nor `scoring`.
 * · VETO THE QUESTION WORDS. "what is our new business scoring process?" carries a MAKE term
 *   (`new`) and an ARTEFACT (`process`) and would otherwise draw a graph in answer to a
 *   question, in front of a customer.
 * · DO NOT VETO THE AUXILIARIES. `are`, `do`, `can` live inside perfectly good build
 *   sentences — "design a process to make sure claim files are complete" is not a question.
 * · `agentic` IS NOT `agent`. The matcher is whole-word, so vetoing `agent` on the build
 *   family leaves "an agentic process" untouched. That is the same word-boundary fact that
 *   makes the surface router read it as a process, and it is measured, not assumed.
 */

/** "Make me one of these." Broader than the surface router's MAKE_RE, and not anchored. */
const MAKE = [
  'build',
  'builds',
  'building',
  'build me',
  'build us',
  'create',
  'creates',
  'creating',
  'create me',
  'make',
  'makes',
  'making',
  'make me',
  'draft',
  'drafts',
  'design',
  'designs',
  'set up',
  'sets up',
  'setting up',
  'stand up',
  'spin up',
  'put together',
  'assemble',
  'automate',
  'give me',
  'i need',
  'i want',
  'we need',
  'we want',
  'i would like',
  'add',
  'new',
  'commission',
  'commissions',
  'commissioned',
  'stands up',
];

/**
 * Nothing here is an instruction to make something. Vetoes are cheap — the intent pass runs
 * only on a total keyword miss — so they are spent freely on the shapes that would embarrass.
 */
const NOT_A_MAKE = [
  // beats that own their own sentences
  'apply',
  'applied',
  'recommendation',
  'recommendations',
  'migrate',
  'migrating',
  // the self-account question belongs to whichever agent was asked
  'yourself',
  'your own',
  'itself',
  'its own',
];
/*
 * THE QUESTION WORDS ARE NOT ON THAT LIST, AND THAT IS MEASURED RATHER THAN RELAXED.
 * Vetoing `what`/`which`/`how` anywhere in the sentence killed eleven perfectly good
 * instructions in the corpus — "design an intake process that checks the documents
 * against what the benefit actually requires", "create a process that decides which
 * claims go to the special investigations unit". People say `what` in the middle of an
 * instruction constantly. What must never build is a sentence that OPENS as a question,
 * and that is decided once, positionally, in `findScenario` — see `opensAsAQuestion`.
 */

/** The thing a build sentence asks for. */
const PROCESS_NOUN = [
  'process',
  'processes',
  'workflow',
  'workflows',
  'pipeline',
  'flow',
  'procedure',
  'automation',
  'chain',
];

/** The thing a commission sentence asks for. */
const AGENT_NOUN = [
  'agent',
  'agents',
  'sentinel',
  'sentinels',
  'watcher',
  'watchers',
  'monitor',
  'guardian',
];

/**
 * A build is not an agent sentence and an agent sentence is not a build. Each family vetoes
 * the other's head nouns, which is what makes "build an autonomous agent called SIU Triage
 * Sentinel" a commission and "create an agentic process" a build, whatever verb opened them.
 */
/*
 * The cross-vetoes are HEAD NOUNS, not the whole opposite list, and the asymmetry is
 * measured. `workflow` turns up as context inside agent sentences constantly — "an agent
 * that watches what happens after the workflows have finished", "across all five claims
 * workflows" — so vetoing it would have killed four of the integrity sentinel's phrasings.
 * `process` does not behave that way: when somebody says process they are asking for one.
 * In the other direction `agent` is common inside build sentences ("an agentic process" is
 * a different token, but "an agent-driven process" is not), so only the unmistakably
 * agent-shaped nouns veto a build.
 */
const NOT_THE_ASK_FOR_A_PROCESS = ['sentinel', 'sentinels', 'watcher', 'watchers', 'guardian'];
const NOT_THE_ASK_FOR_AN_AGENT = ['process', 'processes', 'pipeline', 'procedure'];

const build = (domain: string[]): Intent => ({
  all: [MAKE, PROCESS_NOUN, domain],
  none: [...NOT_A_MAKE, ...NOT_THE_ASK_FOR_A_PROCESS],
});

const watcher = (domain: string[]): Intent => ({
  all: [MAKE, [...AGENT_NOUN, 'autonomous', 'always-on'], domain],
  none: [...NOT_A_MAKE, ...NOT_THE_ASK_FOR_AN_AGENT],
});

/* ── the five processes ───────────────────────────────────────────────────────── */

export const MAKE_COMPLETENESS = build([
  'completeness',
  'complete',
  'incomplete',
  'missing',
  'missing-document',
  'document',
  'documents',
  'docs',
  'documentation',
  'paperwork',
  'attachment',
  'attachments',
  'chase',
  'chasing',
  'outstanding',
  'requirement',
  'requirements',
]);

export const MAKE_FRAUD = build([
  'fraud',
  'fraudulent',
  'fraud-detection',
  'anti-fraud',
  'siu',
  'siu-triage',
  'special investigations',
  'financial crime',
  'indicator',
  'indicators',
  'suspicious',
  'investigators',
  'triage',
]);

export const MAKE_LAPSE = build([
  'lapse',
  'lapses',
  'lapsing',
  'lapsed',
  'retention',
  'churn',
  'persistency',
  'at-risk',
  'at risk',
  'in-force',
  'adviser',
  'advisers',
  'advisor',
  'advisors',
  'cancel',
  'cancelled',
  'cancellation',
  'surrender',
  'grace period',
]);

export const MAKE_PAYMENT = build([
  // NOT bare `payment`/`payments`: "premium payment behaviour" is a lapse sentence, and
  // the canonical build prompt carries the `payment authorisation` keyword anyway.
  'payout',
  'payouts',
  'settlement',
  'settlements',
  'disbursement',
  'disbursements',
  'payee',
  'dual control',
  'dual-control',
  'authorise',
  'authorises',
  'authorising',
  'authorisation',
  'authorize',
  'authorizes',
  'authorizing',
  'authorization',
  'treasury',
  "payee's",
  'bank account',
  'signature',
  'signatures',
  'approver',
  'approvers',
  'sanctions',
]);

export const MAKE_UWSCORING = build([
  'underwriting',
  'underwrite',
  'underwriter',
  'underwriters',
  'uw',
  'new business',
  'application',
  'applications',
  'proposal',
  'proposals',
  'rating',
  'risk-score',
  'risk scoring',
  'risk-scoring',
  'indicative',
]);

/* ── the watchers ─────────────────────────────────────────────────────────────── */

/** The SIU Triage Sentinel — born on stage. */
export const MAKE_SIU_SENTINEL = watcher([
  'siu',
  'siu-triage',
  'referral queue',
  'referral list',
  'referrals',
  'special investigations',
  'financial crime',
  'fraud referral',
]);

/** The Claims Integrity Sentinel — born on stage. */
/*
 * `between the runs` is NOT here, and neither is a bare `the book`. Both sentinels say
 * "between the runs" — it is in the SIU sentinel's own canonical sentence — and `the book`
 * is what a retention sentence calls the in-force book. Either one makes two watcher
 * intents match, and two matching answers nothing.
 */
export const MAKE_INTEGRITY_SENTINEL = watcher([
  'integrity',
  'claims book',
  'across the workflows',
  'the workflows',
  'five workflows',
  'claims workflows',
]);

/* ── the four already commissioned, so the same phrasing finds them ───────────── */

export const MAKE_COMPLETENESS_SENTINEL = watcher([
  'completeness',
  'complete',
  'incomplete',
  'missing document',
  'missing documents',
  'requirement',
  'requirements',
  'document chase',
]);

export const MAKE_RETENTION_SENTINEL = watcher([
  'retention',
  'lapse',
  'lapses',
  'lapsing',
  'at-risk',
  'at-risk flags',
  'at-risk flag',
  // NOT a bare `flags`: "flags clearances recorded with an empty reason field" is the SIU
  // sentinel's job described with `flags` as a VERB, and it made two watchers match.
  'outreach',
  'persistency',
  'in-force',
]);

export const MAKE_SETTLEMENT_SENTINEL = watcher([
  'settlement',
  'settlements',
  'payment',
  'payments',
  'payout',
  'payouts',
  'dual control',
  'dual-control',
  'approver',
  'approvers',
  'treasury',
  'second look',
]);

export const MAKE_UNDERWRITING_SCORER = watcher([
  'underwriting',
  'underwrite',
  'underwriter',
  'underwriters',
  'uw',
  'new business',
  'rating basis',
  'rating',
  'application',
  'applications',
]);
